import {makeAutoObservable, reaction, runInAction} from 'mobx'
import Agent from '../../api/Agent'
import {ITrainingNote, TrainingNoteFormValues} from '../../models/Training/TrainingNote'
import {toast} from 'react-toastify'
import {RootStore} from '../RootStore'
import dayjs from 'dayjs'

export default class TrainingNoteStore {
    rootStore: RootStore

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore
        makeAutoObservable(this)

        reaction(
            () => this.predicate.keys(),
            () => {
                this.page = 0
                this.trainingNoteRegistry.clear()
                this.loadTrainingNote()
            }
        )
        reaction(
            () => this.currentTrainingId,
            () => {

                this.page = 0
                this.trainingNoteRegistry.clear()
                this.loadTrainingNote()
            }
        )
    }

    trainingNoteRegistry = new Map()
    trainingNote: ITrainingNote | null = null
    editMode = false


    currentTrainingId = '';
    trainingNoteCount = 0
    page = 0
    limit = 5

    predicate = new Map()

    setCurrentTrainingId = (trainingId: string) => {
        this.currentTrainingId = trainingId;
    }

    setPredicate = (predicate?: string, value?: any) => {
        if (!!predicate) {
            if (predicate !== 'all') {
                this.predicate.delete(predicate)
                if (!!value) this.predicate.set(predicate, value instanceof Date ? new Date(value).toISOString() : value)
            } else {
                this.predicate.clear()
            }
        }
    }

    get axiosParams() {
        const params = new URLSearchParams()
        params.append('limit', String(this.limit))
        params.append('offset', `${this.page ? this.page * this.limit : 0}`)
        this.predicate.forEach((value, key) => {
            if (!!value) params.append(key, value)
        })
        return params
    }

    get totalPages() {
        return Math.ceil(this.trainingNoteCount / this.limit)
    }

    get trainingNotesById() {
        return Array.from(this.trainingNoteRegistry.values()).sort((a, b) => b.id - a.id)
    }

    get trainingNotesByDate() {
        return Array.from(this.trainingNoteRegistry.values()).sort(
            (a, b) => dayjs(b.updatedOn).isBefore(a.updatedOn) ? -1 : 1//   Date.parse(a.SubmittedOn)-Date.parse(b.SubmittedOn)
        )
    }

    getTrainingNote = (id: string) => {
        return this.trainingNoteRegistry.get(id)
    }

    setPage = (page: number) => {
        this.page = page
    }

    setLimit = (limit: number) => {
        this.limit = limit
    }

    handleChangePage = (newPage: number, newRow: number) => {
        //sets page, we do -1 because the offset will be multiplied against 
        this.setPage(newPage - 1)
        //sets the row limit
        this.setLimit(newRow)
        //clears all records in the registry so that next page didnt load the prev records
        this.trainingNoteRegistry.clear()
        this.loadTrainingNote()
    }

    clearTrainingNote = () => {
        this.trainingNoteRegistry.clear()
    }

    loadTrainingNote = async () => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            if (!!this.currentTrainingId) {
                const trainingNotesEnvelope = await Agent.TrainingNotes.byTrainingId(this.currentTrainingId, this.axiosParams);
                const {modelsDto, modelCount} = trainingNotesEnvelope;
                runInAction(() => {
                    modelsDto.forEach((trainingNote: ITrainingNote) => {
                        this.trainingNoteRegistry.set(trainingNote.id, trainingNote)
                    })
                    this.trainingNoteCount = modelCount
                })
            }
        } catch (error) {
            throw error
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    getTrainingNoteById = async (id: string) => {
        let trainingNote = this.getTrainingNote(id)
        if (trainingNote) {
            this.trainingNote = trainingNote
            return trainingNote
        } else {
            let loadingId = this.rootStore.commonStore.setIsLoading()
            try {
                trainingNote = await Agent.TrainingNotes.byId(id)
                runInAction(() => {
                    this.trainingNote = trainingNote
                    this.trainingNoteRegistry.set(trainingNote.id, trainingNote)
                    this.rootStore.commonStore.setIsLoading(loadingId)
                })
                return trainingNote
            } catch (error) {
                this.rootStore.errorStore.addError(error)
            }
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    createTrainingNote = async (trainingNote: ITrainingNote) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            const newTrainingNote = await Agent.TrainingNotes.create(trainingNote)
            await this.loadTrainingNote()
            runInAction(() => {
                this.trainingNoteRegistry.set(newTrainingNote.id, newTrainingNote)
                this.rootStore.modalStore.closeModal('NoteModal')
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            toast.success('Saved')
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    editTrainingNote = (trainingNote: TrainingNoteFormValues) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        Agent.TrainingNotes.update(trainingNote).then((updatedTrainingNote) => {
            runInAction(() => {
                this.trainingNoteRegistry.set(updatedTrainingNote.id, updatedTrainingNote)
                this.rootStore.modalStore.closeModal('NoteModal')
            })
            toast.success('Saved')
        }).catch(error => {
            this.rootStore.errorStore.addError(error)
        }).finally(() => this.rootStore.commonStore.setIsLoading(loadingId))


    }

    deleteTrainingNote = async (trainingNote: ITrainingNote) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            await Agent.TrainingNotes.delete(trainingNote.id)
            runInAction(() => {
                this.trainingNoteRegistry.delete(trainingNote.id)
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            toast.success('Saved')

        } catch (error) {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            this.rootStore.errorStore.addError(error)
        }

    }


}